import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Star, User, LogOut, Settings, Menu, X, MapPin, Check
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color, visited) => {
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path fill="${color}" stroke="${visited ? '#FFD700' : '#333'}" stroke-width="2" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/>
      <circle fill="${visited ? '#FFD700' : '#fff'}" cx="12" cy="12" r="6"/>
    </svg>
  `;
  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -36],
  });
};

const getStarColor = (stars) => {
  switch (stars) {
    case 3: return '#FFD700'; // Gold
    case 2: return '#C0C0C0'; // Silver
    default: return '#CD7F32'; // Bronze
  }
};

// Component to fit bounds to markers
const MAP_CENTER = [48.5, 10];
const MAP_STYLE = { height: 'calc(100vh - 64px)', width: '100%' };

const FitBounds = ({ restaurants }) => {
  const map = useMap();
  
  useEffect(() => {
    if (restaurants.length > 0) {
      const bounds = L.latLngBounds(
        restaurants.map(r => [r.latitude, r.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [restaurants, map]);
  
  return null;
};

const RestaurantMap = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Filters
  const [showVisited, setShowVisited] = useState(true);
  const [showUnvisited, setShowUnvisited] = useState(true);
  const [starFilter, setStarFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API}/restaurants/map`, { withCredentials: true });
      setRestaurants(response.data);
      
      // Extract unique countries
      const uniqueCountries = [...new Set(response.data.map(r => r.country))].sort();
      setCountries(uniqueCountries);
    } catch (error) {
      console.warn("Error fetching restaurants:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRestaurants = restaurants.filter(r => {
    if (r.latitude == null || r.longitude == null) return false;
    if (!showVisited && r.visited) return false;
    if (!showUnvisited && !r.visited) return false;
    if (starFilter !== "all" && r.stars !== parseInt(starFilter)) return false;
    if (countryFilter !== "all" && r.country !== countryFilter) return false;
    return true;
  });

  const navItems = [
    { label: t('common.dashboard'), path: '/dashboard' },
    { label: t('common.restaurants'), path: '/restaurants' },
    { label: t('common.preferences'), path: '/preferences' },
    { label: t('common.community'), path: '/community' },
    { label: t('common.leaderboard'), path: '/leaderboard' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="map-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-[1000]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Star className="w-6 h-6 text-primary fill-primary" />
              <span className="text-xl font-bold gradient-text">Étoile</span>
            </div>

            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('common.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted/50" />
                  <DropdownMenuItem onClick={() => logout()} className="cursor-pointer text-red-400">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="relative">
        {/* Filters Panel */}
        <Card className="absolute top-4 left-4 z-[1000] bg-card/95 backdrop-blur-sm border-border w-72">
          <CardContent className="p-4 space-y-4">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2 mb-1">
                <MapPin className="w-5 h-5 text-primary" />
                {t('map.title')}
              </h2>
              <p className="text-xs text-muted-foreground">{t('map.subtitle')}</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm">{t('map.showVisited')}</Label>
                <Switch checked={showVisited} onCheckedChange={setShowVisited} />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">{t('map.showUnvisited')}</Label>
                <Switch checked={showUnvisited} onCheckedChange={setShowUnvisited} />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{t('map.filterByStars')}</Label>
              <Select value={starFilter} onValueChange={setStarFilter}>
                <SelectTrigger className="bg-transparent border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">{t('map.allStars')}</SelectItem>
                  <SelectItem value="3">⭐⭐⭐ 3 Stars</SelectItem>
                  <SelectItem value="2">⭐⭐ 2 Stars</SelectItem>
                  <SelectItem value="1">⭐ 1 Star</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{t('map.filterByCountry')}</Label>
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger className="bg-transparent border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-[200px]">
                  <SelectItem value="all">{t('map.allCountries')}</SelectItem>
                  {countries.map(country => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="pt-2 border-t border-border">
              <p className="text-sm text-muted-foreground">
                <span className="font-bold text-primary">{filteredRestaurants.length}</span> {t('map.totalRestaurants')}
              </p>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-[#FFD700]" />
                  {t('map.visited')}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-white border border-gray-400" />
                  {t('map.notVisited')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Map */}
        {loading ? (
          <div className="h-[calc(100vh-64px)] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <MapContainer
            center={MAP_CENTER}
            zoom={5}
            style={MAP_STYLE}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            <FitBounds restaurants={filteredRestaurants} />
            
            {filteredRestaurants.map((restaurant) => (
              <Marker
                key={restaurant.restaurant_id}
                position={[restaurant.latitude, restaurant.longitude]}
                icon={createCustomIcon(getStarColor(restaurant.stars), restaurant.visited)}
              >
                <Popup className="custom-popup">
                  <div className="p-1 min-w-[200px]">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-sm">{restaurant.name}</h3>
                      {restaurant.visited && (
                        <span className="bg-primary/20 text-primary text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          {t('map.visited')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mb-1">
                      {restaurant.location}, {restaurant.country}
                    </p>
                    <div className="flex gap-0.5 mb-2">
                      {[...Array(restaurant.stars)].map((_, i) => (
                        <Star key={`map-star-${restaurant.restaurant_id}-${i}`} className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{restaurant.cuisine_type}</p>
                    <Button
                      size="sm"
                      className="w-full text-xs h-7"
                      onClick={() => navigate('/restaurants')}
                    >
                      {restaurant.visited ? t('map.viewDetails') : t('map.recordVisit')}
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </main>

      <style>{`
        .custom-marker {
          background: none;
          border: none;
        }
        .leaflet-popup-content-wrapper {
          background: #1a1a1a;
          color: white;
          border-radius: 8px;
        }
        .leaflet-popup-tip {
          background: #1a1a1a;
        }
        .leaflet-popup-content {
          margin: 8px;
        }
      `}</style>
    </div>
  );
};

export default RestaurantMap;
